/**
 * Blog Service
 *
 * Handles all blog post operations (public and admin)
 */

import api from './api';
import type {
  BlogPost,
  BlogPostListResponse,
  BlogPostResponse,
  BlogCategoriesResponse,
  CreateBlogPostData,
  UpdateBlogPostData,
} from '../types/blog';

/**
 * PUBLIC BLOG ENDPOINTS
 */

/**
 * Get all published blog posts
 * @param category - Optional category filter
 */
export const getPublicBlogPosts = async (language = 'en', category?: string): Promise<BlogPost[]> => {
  const params: Record<string, string> = { language };
  if (category && category !== 'All Posts') params.category = category;
  const response = await api.get<BlogPost[]>('/api/blog', { params });
  return response.data;
};

/**
 * Get homepage-featured published posts (up to 3)
 */
export const getFeaturedBlogPosts = async (language = 'en'): Promise<BlogPost[]> => {
  const response = await api.get<BlogPost[]>('/api/blog/featured', { params: { language } });
  return response.data;
};

/**
 * Get a single published blog post by slug
 */
export const getPublicBlogPost = async (slug: string, language = 'en'): Promise<BlogPost> => {
  const response = await api.get<BlogPost>(`/api/blog/${slug}`, { params: { language } });
  return response.data;
};

/**
 * Get all unique categories from published posts
 */
export const getPublicBlogCategories = async (language = 'en'): Promise<string[]> => {
  const response = await api.get<string[]>('/api/blog/categories', { params: { language } });
  return response.data;
};

/**
 * ADMIN BLOG ENDPOINTS
 */

/**
 * Get all blog posts (admin - includes drafts)
 * @param status - Optional status filter ('draft' or 'published')
 * @param category - Optional category filter
 * @param page - Optional page number for pagination
 */
export const getAllBlogPosts = async (params?: {
  status?: 'draft' | 'published';
  category?: string;
  page?: number;
}): Promise<BlogPostListResponse> => {
  const response = await api.get<BlogPostListResponse>('/api/admin/blog-posts', {
    params,
  });
  return response.data;
};

/**
 * Get a single blog post (admin - includes drafts)
 */
export const getBlogPost = async (id: number): Promise<BlogPost> => {
  const response = await api.get<BlogPost>(`/api/admin/blog-posts/${id}`);
  return response.data;
};

/**
 * Create a new blog post
 */
export const createBlogPost = async (data: CreateBlogPostData): Promise<BlogPostResponse> => {
  const response = await api.post<BlogPostResponse>('/api/admin/blog-posts', data);
  return response.data;
};

/**
 * Update a blog post
 */
export const updateBlogPost = async (
  id: number,
  data: UpdateBlogPostData
): Promise<BlogPostResponse> => {
  const response = await api.put<BlogPostResponse>(`/api/admin/blog-posts/${id}`, data);
  return response.data;
};

/**
 * Delete a blog post
 */
export const deleteBlogPost = async (id: number): Promise<void> => {
  await api.delete(`/api/admin/blog-posts/${id}`);
};

/**
 * Get all unique categories (admin - includes all categories)
 */
export const getBlogCategories = async (): Promise<string[]> => {
  const response = await api.get<string[]>('/api/admin/blog-posts/categories');
  return response.data;
};

/**
 * Export service object for convenience
 */
const blogService = {
  // Public
  getPublicBlogPosts,
  getFeaturedBlogPosts,
  getPublicBlogPost,
  getPublicBlogCategories,
  // Admin
  getAllBlogPosts,
  getBlogPost,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  getBlogCategories,
};

export default blogService;
